import React from "react";
import { Link } from "../../../design/link";
import { Text } from "../../../design/text";
import { Heading, Subheading } from "../../../design/heading";
import { Card } from "../../../design/card";
import {
  BuildingOffice2Icon,
  UserIcon,
  CreditCardIcon,
  PuzzlePieceIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  DeviceTabletIcon,
  BanknotesIcon,
} from "@heroicons/react/20/solid";
import { useAccount } from "../../../contexts/AccountContext";
import ThemeToggle from "../../../components/ThemeToggle";

interface SettingCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  iconColor?: string;
}

const SettingCard: React.FC<SettingCardProps> = ({
  title,
  description,
  icon,
  route,
  iconColor = "text-blue-600",
}) => (
  <Link href={route} className="block h-full">
    <Card className="h-full cursor-pointer group hover:shadow-xl hover:scale-[1.02] transition-all duration-200 border-l-4 border-l-transparent hover:border-l-blue-500">
      <div className="flex items-start justify-between">
        <div className="flex items-start flex-1 space-x-4">
          <div className={`${iconColor} p-2 rounded-lg bg-gray-50 dark:bg-zinc-800 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors duration-200`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <Subheading className="text-gray-900 transition-colors duration-200 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
              {title}
            </Subheading>
            <Text className="mt-1 text-gray-600 dark:text-gray-300">
              {description}
            </Text>
          </div>
        </div>
        <ChevronRightIcon className="flex-shrink-0 w-5 h-5 text-gray-400 transition-colors duration-200 group-hover:text-blue-500" />
      </div>
    </Card>
  </Link>
);

const SettingsHome: React.FC = () => {
  const {account} = useAccount();
  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      {/* Header Section */}
      <div className="mb-8">
        <Heading level={1} className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">
          Business Settings
        </Heading>
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <span>Account:</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {account?.id ?? "Account unavailable"}
          </span>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <SettingCard
          title="Business"
          description="Business details, invoicing, and company information."
          icon={<BuildingOffice2Icon className="w-6 h-6" />}
          route="/settings/business"
          iconColor="text-emerald-600"
        />
        <SettingCard
          title="Billing & Plans"
          description="Manage your subscription, payment methods, and billing history."
          icon={<CreditCardIcon className="w-6 h-6" />}
          route="/settings/billing"
          iconColor="text-blue-600"
        />
        <SettingCard
          title="Profile"
          description="Personal details, password, communication preferences, and active sessions."
          icon={<UserIcon className="w-6 h-6" />}
          route="/settings/user"
          iconColor="text-purple-600"
        />
        <SettingCard
          title="Integrations"
          description="Connect Visit Proof with other services and third-party tools."
          icon={<PuzzlePieceIcon className="w-6 h-6" />}
          route="/settings/integrations"
          iconColor="text-indigo-600"
        />
        <SettingCard
          title="PDF Reports"
          description="Configure event report PDF settings, including content, timezone, and formatting."
          icon={<DocumentTextIcon className="w-6 h-6" />}
          route="/settings/reports"
          iconColor="text-rose-600"
        />
        <SettingCard
          title="Email"
          description="Send notifications from your own domain instead of info@visitproof.com."
          icon={<EnvelopeIcon className="w-6 h-6" />}
          route="/settings/email"
          iconColor="text-sky-600"
        />
        <SettingCard
          title="Check-In Kiosk"
          description="Customize branding, signatures, and photo capture for the check-in tablet."
          icon={<DeviceTabletIcon className="w-6 h-6" />}
          route="/settings/checkin"
          iconColor="text-teal-600"
        />
        <SettingCard
          title="Payments"
          description="Accept online visit payments through Stripe, including prepayment before visits."
          icon={<BanknotesIcon className="w-6 h-6" />}
          route="/settings/payments"
          iconColor="text-emerald-600"
        />
      </div>

      {/* Theme Toggle Section */}
      <div className="mt-12">
        <Heading level={2} className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          Appearance
        </Heading>
        <div className="p-4 bg-white border border-gray-200 rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
          <ThemeToggle />
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="mt-12">
        <Heading level={2} className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          Quick Actions
        </Heading>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Link
            href="/settings/business"
            className="flex items-center justify-center p-4 transition-colors duration-200 bg-white border border-gray-200 rounded-lg dark:bg-zinc-800 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700"
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Update Business Info</span>
          </Link>
          <Link
            href="/settings/billing"
            className="flex items-center justify-center p-4 transition-colors duration-200 bg-white border border-gray-200 rounded-lg dark:bg-zinc-800 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700"
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View Billing</span>
          </Link>
          <Link
            href="/settings/user"
            className="flex items-center justify-center p-4 transition-colors duration-200 bg-white border border-gray-200 rounded-lg dark:bg-zinc-800 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700"
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Change Password</span>
          </Link>
          <Link
            href="/settings/integrations"
            className="flex items-center justify-center p-4 transition-colors duration-200 bg-white border border-gray-200 rounded-lg dark:bg-zinc-800 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700"
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Add Integration</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SettingsHome;
